import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdjustPoints,
  LoyaltyLedgerEntry,
  LoyaltyMember,
  LoyaltyProgram,
  RedeemPoints,
  RedeemPointsResult,
  UpsertLoyaltyProgram,
} from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS-13 Feature 2 — loyalty / points (LRU-06). TanStack Query over the generated client, mirroring
 * data/vouchers.ts (server-derived balances, idempotent money-adjacent mutations) and data/stock.ts
 * (config read + member view + append-only ledger).
 *
 * HONESTY (non-negotiable): points are a NON-MONETARY perk, but a member's BALANCE is DERIVED from an
 * append-only signed-points ledger (the server's `balancePoints` = signed sum, never a stored column),
 * and a REDEEM converts points→pence on the server (computeDiscount) and writes a standard `discount`
 * line (reasonCode 'loyalty'). The UI DISPLAYS the server's figures and POSTs intent — it never sums a
 * balance or computes a points→£ reduction locally. Redeem/adjust are IDEMPOTENT on a client-supplied
 * `idempotencyKey`: the caller mints it once per intent and gates the button on `isPending`, so a
 * double-tap is a safe replay that moves no points/money. 422/409 surface faithfully through the shared
 * ApiClientError envelope (e.g. program disabled, balance < points, points < minRedeemPoints).
 *
 * Because a redeem produces a discount line that reduces a till basket, after a redeem we invalidate the
 * member AND the receipt/till keys (payments + receipts) so the checkout re-reads from the server.
 */

// ── Reads ──────────────────────────────────────────────────────────────────────────

/**
 * The per-salon loyalty program config — one row; the API returns INERT defaults (enabled:false) when a
 * salon has never set one, so there is no 404 to map. Money-adjacent (drives the points→£ rate), so it
 * reads live (staleTime 0); a genuine error surfaces via `isError`, which the panel renders honestly.
 */
export function useLoyaltyProgram(enabled = true) {
  return useQuery({
    queryKey: qk.loyaltyProgram,
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<LoyaltyProgram> => unwrap(await api.GET('/v1/loyalty/program')),
  });
}

/**
 * One member's view: their DERIVED `balancePoints` (the server's signed sum of the ledger — never a
 * stored column) + their append-only history. Gated on a clientId so a closed strip/modal doesn't fire.
 * Reads live (staleTime 0): a balance is the source of truth the till and the client record re-read.
 */
export function useLoyaltyMember(clientId: string | null) {
  return useQuery({
    queryKey: clientId ? qk.loyaltyMember(clientId) : ['loyalty', 'member', 'none'],
    enabled: Boolean(clientId),
    staleTime: 0,
    queryFn: async (): Promise<LoyaltyMember> =>
      unwrap(
        await api.GET('/v1/loyalty/members/{clientId}', {
          params: { path: { clientId: clientId! } },
        }),
      ),
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────────

/**
 * Upsert the per-salon program config (manager + on the API). All fields optional — a partial update.
 * onSuccess re-bases the program (and so the till's redeem rate); we also nudge every member view since
 * the rate they redeem at lives in the program.
 */
export function useUpsertLoyaltyProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpsertLoyaltyProgram): Promise<LoyaltyProgram> =>
      unwrap(await api.PUT('/v1/loyalty/program', { body: body as never })),
    onSuccess: (program) => {
      qc.setQueryData(qk.loyaltyProgram, program);
      void qc.invalidateQueries({ queryKey: ['loyalty'] });
    },
  });
}

/**
 * Redeem points at the till — the server validates (program enabled, balance >= points >=
 * minRedeemPoints), converts points→pence (computeDiscount) and writes a `discount` line + a debit
 * ledger entry in one tx under a member-row lock. Idempotent on `idempotencyKey` (a retry returns the
 * original, moves no points). Returns the discount line + the member's NEW derived balance. Because the
 * redeem produced a discount against a basket, invalidate the member AND any receipt/till keys.
 */
export function useRedeemPoints() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RedeemPoints): Promise<RedeemPointsResult> =>
      unwrap(await api.POST('/v1/loyalty/redeem', { body: body as never })),
    onSuccess: (_result, body) => {
      // The new balance is in the result, but the history changed too — re-read the member view, and the
      // till/receipt ledgers that the discount line now appears in.
      void qc.invalidateQueries({ queryKey: qk.loyaltyMember(body.clientId) });
      void qc.invalidateQueries({ queryKey: ['payments'] });
      void qc.invalidateQueries({ queryKey: ['receipts'] });
      void qc.invalidateQueries({ queryKey: ['discounts'] });
    },
  });
}

/**
 * A manual signed points adjustment by a manager (the audit trail — no silent balance edits). `points`
 * is signed (positive credit / negative debit, never zero); `reason` is mandatory; append-only. Returns
 * the new ledger entry. onSuccess re-reads the member view (the derived balance + history both moved).
 */
export function useAdjustPoints() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdjustPoints): Promise<LoyaltyLedgerEntry> =>
      unwrap(await api.POST('/v1/loyalty/adjust', { body: body as never })),
    onSuccess: (_entry, body) => {
      void qc.invalidateQueries({ queryKey: qk.loyaltyMember(body.clientId) });
    },
  });
}
